/**
 * FreightlinksChargesAdmin
 * ─────────────────────────────────────────────────
 * Admin panel for managing the freightlinks_charges collection that
 * feeds the Port-Pair Search panel. Two key actions:
 *   1. "Seed defaults" — quick baseline so Port-Pair never shows empty
 *   2. "Run scraper" — best-effort Playwright job against
 *      portal.freightlinks.net (background task, poll /status)
 *
 * Also lists current rows + lets staff upload a CSV.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API_URL } from "../config/api";
import { Button } from "./ui/button";
import {
  RefreshCw, Sparkles, Upload, PlayCircle, Loader2, Trash2, Database,
} from "lucide-react";

export default function FreightlinksChargesAdmin() {
  const token = (typeof window !== "undefined" && localStorage.getItem("token")) || "";
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);
  const [running, setRunning] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [list, st] = await Promise.all([
        axios.get(`${API_URL}/freightlinks-charges/list`, { headers }),
        axios.get(`${API_URL}/freightlinks-charges/status`, { headers }),
      ]);
      setItems(list.data?.items || []);
      setStatus(st.data || null);
      setRunning(!!st.data?.running);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load charges");
    } finally {
      setLoading(false);
    }
  }, [headers]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Poll while a scrape is in flight
  useEffect(() => {
    if (!running) return undefined;
    const id = setInterval(async () => {
      try {
        const st = await axios.get(`${API_URL}/freightlinks-charges/status`, { headers });
        setStatus(st.data);
        if (!st.data?.running) {
          setRunning(false);
          fetchAll();
          const last = st.data?.last_result;
          if (last?.ok) {
            toast.success(`Scrape OK — ${last.rows_origin} origin + ${last.rows_destination} destination`);
          } else {
            toast.error(`Scrape failed: ${(last?.errors || ["unknown"]).join("; ")}`);
          }
        }
      } catch (e) {
        // ignore poll errors
      }
    }, 4000);
    return () => clearInterval(id);
  }, [running, headers, fetchAll]);

  const seedDefaults = async () => {
    try {
      const res = await axios.post(
        `${API_URL}/freightlinks-charges/seed-defaults?overwrite=true`,
        {}, { headers }
      );
      toast.success(`Seeded — ${res.data.inserted} new, ${res.data.updated} updated`);
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Seed failed");
    }
  };

  const runScrape = async () => {
    try {
      const res = await axios.post(`${API_URL}/freightlinks-charges/scrape`, {}, { headers });
      if (res.data.queued) {
        toast.message("Scrape started in background");
        setRunning(true);
      } else {
        toast.message(res.data.message || "Already running");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Scrape failed");
    }
  };

  const uploadCsv = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const kind = window.prompt("Upload as 'origin' or 'destination' charges?", "origin");
    if (!kind || !["origin", "destination"].includes(kind)) {
      toast.error("Must be 'origin' or 'destination'");
      return;
    }
    const fd = new FormData();
    fd.append("file", file);
    try {
      const res = await axios.post(
        `${API_URL}/freightlinks-charges/upload-csv?kind=${kind}`,
        fd,
        { headers: { ...headers, "Content-Type": "multipart/form-data" } }
      );
      toast.success(`CSV imported — ${res.data.inserted} new, ${res.data.updated} updated`);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      e.target.value = "";
    }
  };

  const deleteRow = async (row) => {
    if (!window.confirm(`Delete ${row.kind} charges for ${row.pol}/${row.pod}?`)) return;
    try {
      await axios.delete(
        `${API_URL}/freightlinks-charges/${row.kind}/${row.pol}/${row.pod}`,
        { headers }
      );
      toast.success("Deleted");
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="freightlinks-charges-admin">
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="flex items-center gap-2">
          <Database className="h-5 w-5 text-blue-600" />
          <h3 className="text-base font-semibold text-slate-900">Freightlinks Charges</h3>
        </div>
        <div className="text-xs text-slate-500">
          Feeds the Port-Pair Search "Origin charges" + "Destination charges" blocks.
        </div>
        <div className="ml-auto flex items-center gap-2 flex-wrap">
          <Button variant="outline" size="sm" onClick={fetchAll} disabled={loading} data-testid="fl-charges-refresh">
            <RefreshCw className={`h-4 w-4 mr-1 ${loading ? "animate-spin" : ""}`} /> Refresh
          </Button>
          <Button variant="outline" size="sm" onClick={seedDefaults} data-testid="fl-charges-seed">
            <Sparkles className="h-4 w-4 mr-1" /> Seed defaults
          </Button>
          <label className="cursor-pointer">
            <input type="file" accept=".csv,text/csv" className="hidden" onChange={uploadCsv} data-testid="fl-charges-csv-input" />
            <span className="inline-flex items-center px-3 py-1.5 text-xs font-medium rounded border border-slate-300 bg-white hover:bg-slate-50">
              <Upload className="h-4 w-4 mr-1" /> Upload CSV
            </span>
          </label>
          <Button size="sm" onClick={runScrape} disabled={running} className="bg-blue-600 hover:bg-blue-700" data-testid="fl-charges-scrape">
            {running ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <PlayCircle className="h-4 w-4 mr-1" />}
            {running ? "Scraping…" : "Run scraper"}
          </Button>
        </div>
      </div>

      {/* Status strip */}
      {status?.last_result && (
        <div className={`rounded-lg border px-3 py-2 mb-3 text-xs ${status.last_result.ok ? "border-emerald-200 bg-emerald-50 text-emerald-800" : "border-amber-200 bg-amber-50 text-amber-800"}`} data-testid="fl-charges-status">
          Last run: {status.last_run || "—"}  ·  {status.last_result.ok ? "OK" : "Failed"}  ·  origin: {status.last_result.rows_origin} · destination: {status.last_result.rows_destination}
          {status.last_result.errors?.length > 0 && (
            <span className="ml-2 italic">— {status.last_result.errors.join("; ")}</span>
          )}
        </div>
      )}

      {/* List */}
      {loading ? (
        <div className="text-center py-8 text-slate-500 text-sm">Loading…</div>
      ) : items.length === 0 ? (
        <div className="text-center py-8 text-slate-500 text-sm">
          No charges saved yet. Click "Seed defaults" to populate fallback values.
        </div>
      ) : (
        <div className="overflow-x-auto border border-slate-200 rounded-lg">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs text-slate-700">
              <tr>
                <th className="px-3 py-2 text-left">Scope</th>
                <th className="px-3 py-2 text-left">Kind</th>
                <th className="px-3 py-2 text-left">POL</th>
                <th className="px-3 py-2 text-left">POD</th>
                <th className="px-3 py-2 text-right">#Charges</th>
                <th className="px-3 py-2 text-left">Source</th>
                <th className="px-3 py-2 text-left">Updated</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((r, idx) => (
                <tr key={`${r.kind}-${r.pol}-${r.pod}-${idx}`} className="border-t" data-testid={`fl-charges-row-${idx}`}>
                  <td className="px-3 py-2">
                    {r._kind === "default" ? (
                      <span className="px-1.5 py-0.5 bg-blue-100 text-blue-800 rounded text-xs font-medium">default</span>
                    ) : (
                      <span className="text-slate-500">per-port</span>
                    )}
                  </td>
                  <td className="px-3 py-2 font-medium">{r.kind}</td>
                  <td className="px-3 py-2 font-mono text-xs">{r.pol}</td>
                  <td className="px-3 py-2 font-mono text-xs">{r.pod}</td>
                  <td className="px-3 py-2 text-right">{(r.charges || []).length}</td>
                  <td className="px-3 py-2 text-xs text-slate-500">{r.source || "—"}</td>
                  <td className="px-3 py-2 text-xs text-slate-500">
                    {(r.updated_at || r.scraped_at || r.seeded_at || "").slice(0, 19).replace("T", " ")}
                  </td>
                  <td className="px-3 py-2 text-right">
                    {r._kind !== "default" && (
                      <button onClick={() => deleteRow(r)} className="text-red-500 hover:text-red-700" data-testid={`fl-charges-delete-${idx}`} title="Delete">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="mt-3 text-xs text-slate-400 italic">
        CSV format: <code>pol,pod,code,label,amount,currency,basis</code> · use POL/POD = "ALL" for global defaults.
      </div>
    </div>
  );
}
